import React, { useState, useRef, useEffect } from "react";

/**
 * PriorityPicker Component
 * 
 * Props:
 * - value: string ("Critical", "High", "Medium", "Low")
 * - onChange: (priority: string) => void
 */
export function PriorityPicker({ value, onChange, dropdownAlign = "left" }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Map priority levels to display elements
  const options = [
    { key: "High", label: "High", flagColor: "text-red-500", pillName: "High", activeClass: "text-red-600 bg-red-50 border-red-200 hover:bg-red-100" },
    { key: "Medium", label: "Medium", flagColor: "text-orange-500", pillName: "Medium", activeClass: "text-orange-600 bg-orange-50 border-orange-200 hover:bg-orange-100" },
    { key: "Low", label: "Low", flagColor: "text-gray-400", pillName: "Low", activeClass: "text-gray-500 hover:bg-gray-100 border-gray-300" },
  ];

  // Find active option
  const activeOpt = options.find((o) => o.key === value) || options[2]; // default to Low

  // Decide what style/text to show on the pill
  const isSet = value && value !== "Low";
  const pillText = value || "Low";
  const pillClass = isSet ? activeOpt.activeClass : "text-gray-500 hover:bg-gray-100 border-gray-300";
  const iconColor = isSet ? activeOpt.flagColor : "text-gray-400";

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      {/* Pill Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-200 ${pillClass}`}
      >
        <span className={iconColor}>⚑</span>
        <span>{pillText}</span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute z-50 mt-1 ${dropdownAlign === "right" ? "right-0" : "left-0"} w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 animate-fadeIn`}>
          {options.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => {
                onChange(opt.key);
                setIsOpen(false);
              }}
              className="w-full px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <span className={opt.flagColor}>⚑</span>
                <span>{opt.label}</span>
              </div>
              {value === opt.key && (
                <span className="text-gray-400 font-bold">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
